import { useMemo } from 'preact/hooks';
import { useApiContext, Auth } from './contexts/apiContext';
import { getOffersStore } from './offers-bridge';
import { TrackingEventAttributes, TrackingEventName } from './types';
import { hasRecentPaymentsCheck, markEmptyResult, clearEmptyResult } from './PaymentAlert/atRiskPaymentsCache';

export type Subscription = {
  public_id: string;
  payment: string;
};

export type AtRiskPayment = {
  payment: string;
  risk: {
    state: string;
    reasons: RISK_REASON[];
  };
  last_attempt: { order: string } | null;
  next_retry: { order: string } | null;
};

type Payment = {
  public_id: string;
  token_id: string;
};

type RISK_REASON = 'card_expiry' | 'payment_failure' | 'payment_not_live';

async function authedFetch(auth: Auth, url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: { ...init?.headers, Authorization: JSON.stringify(auth) }
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res;
}

export function useApi() {
  const { auth, environment } = useApiContext();

  return useMemo(
    () => ({
      getAtRiskPayments: async (): Promise<AtRiskPayment[]> => {
        if (hasRecentPaymentsCheck(auth.sig_field)) {
          console.info('Reusing cached payments response');
          return [];
        }
        const res = await authedFetch(auth, `${environment.apiUrl}/customers/${auth.sig_field}/at_risk_payments/`);
        const data = (await res.json()) as { results: AtRiskPayment[] };
        const results = data.results ?? [];
        // store or empty cache. We only cache if there are empty results.
        // We keep checking if payment errors are resolved on each load
        if (results.length === 0) {
          markEmptyResult(auth.sig_field);
        } else {
          // drop any stale empty marker
          clearEmptyResult(auth.sig_field);
        }
        return results;
      },

      getPayment: async (paymentId: string): Promise<Payment> => {
        const res = await authedFetch(auth, `${environment.legoUrl}/payments/${paymentId}/`);
        return res.json();
      },

      sendPaymentEmail: async (paymentToken: string): Promise<void> => {
        // add source=banner so we can identify failed requests in the server logs
        await authedFetch(auth, `${environment.shopifyAppUrl}/api/send-smi-update-payment-email?source=banner`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentId: paymentToken })
        });
      },

      sendTrackingEvent: async <EventName extends TrackingEventName>(
        event: EventName,
        eventAttributes: TrackingEventAttributes[EventName]
      ): Promise<void> => {
        const state = getOffersStore().getState();

        const payload = {
          merchant_public_id: state.merchantId,
          merchant_user_id: auth.sig_field,
          session_id: state.sessionId,
          event_timestamp: Math.floor(Date.now() / 1000),
          event,
          event_attributes: eventAttributes
        };

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        // in dev, point to the preview GTM container if env var is present
        if (import.meta.env.DEV && import.meta.env.VITE_GTM_PREVIEW_HEADER) {
          console.warn(
            'Using GTM preview header. Remove VITE_GTM_PREVIEW_HEADER from your .env file if this is unexpected.'
          );
          headers['X-Gtm-Server-Preview'] = import.meta.env.VITE_GTM_PREVIEW_HEADER;
        }

        await fetch(`${environment.trackingUrl}/c/`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          // events are often sent immediately before a navigation, which cancels in-flight requests
          keepalive: true
        });
      }
    }),
    [auth, environment]
  );
}
